import { useState, useEffect } from 'react';
import { Checkbox } from './ui/checkbox';
import { Button } from './ui/button';
import { Plus, Trash2, Check, X, GripVertical } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { HashtagInput } from './HashtagInput';
import { ActionItem } from '@/types/Meeting';


interface ActionListProps {
  actions: ActionItem[];
  onAddActionItem: (newItem: { text: string; completed: boolean, hashtags: string[] }) => void;
  onDeleteActionItem: (actionItemId: string) => void;
  onReorderAction: (newAction: ActionItem[]) => void;
  onUpdateActionItem: (actionItemId: string, updatedItem: { text: string; completed: boolean, hashtags: string[], order: number }) => void;
  variant?: 'card' | 'classic';
}

interface SortableActionItemProps {
  item: ActionItem;
  onDelete: () => void;
  onEdit: () => void;
  isEditing: boolean;
  editText: string;
  onEditTextChange: (value: string) => void;
  onEditSave: (tags: string[]) => void;
  onEditCancel: () => void;
  onUpdateActionItem: (actionItemId: string, updatedItem: { text: string; completed: boolean, hashtags: string[], order: number }) => void;
  variant?: 'card' | 'classic';
}

function SortableActionItem({
  item,
  onDelete,
  onEdit,
  isEditing,
  editText,
  onEditTextChange,
  onEditSave,
  onEditCancel,
  onUpdateActionItem,
  variant = 'classic',
}: SortableActionItemProps) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item._id });
  const [currentTags, setCurrentTags] = useState<string[]>(item.hashtags);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Conditional styling based on variant
  const alignmentClass = variant === 'card' ? 'items-start' : 'items-center';
  const offsetClass = variant === 'card' ? 'mt-0.5' : '';

  const renderHashtagText = (text: string, hashtags: string[]) => {
    const parts = text.split(/(#\w+)/g);
    return (
      <span>
        {parts.map((part, index) => {
          const tag = part.startsWith('#') ? part.slice(1) : null;
          if (tag && hashtags.includes(tag)) {
            return (
              <span key={index} className="inline-block bg-primary/10 text-primary px-2 py-0.5 rounded-full text-sm">
                {part}
              </span>
            );
          }
          return part;
        })}
      </span>
    );
  };

  return (
    <div ref={setNodeRef} style={style} className={`flex ${alignmentClass} gap-2 px-2 py-1 rounded-lg hover:bg-secondary/50 group`}>
      <button className={`cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground ${offsetClass}`} {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4" />
      </button>
      <Checkbox 
        className={offsetClass}
        checked={item.completed} 
        onCheckedChange={(checked) => 
          onUpdateActionItem(item._id, { 
            ...item, 
            completed: checked as boolean,
            order: item.order ?? 0
          })
        } 
      />
      {isEditing ? (
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <HashtagInput
                value={editText}
                onChange={onEditTextChange}
                onTagsChange={setCurrentTags}
                onEnterPress={() => onEditSave(currentTags)}
                isEditing={true}
              />
            </div>
            <Button variant="ghost" size="icon" onClick={() => onEditSave(currentTags)}>
              <Check className="h-4 w-4 text-green-500" />
            </Button>
            <Button variant="ghost" size="icon" onClick={onEditCancel}>
              <X className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      ) : (
        <>
          <span 
            className={`text-left flex-1 cursor-pointer hover:text-primary ${
              item.completed ? 'line-through text-muted-foreground' : ''
            }`} 
            onClick={onEdit}
          >
            {renderHashtagText(item.text, item.hashtags)}
          </span>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" onClick={onDelete}>
              <Trash2 className="h-4 w-4 text-destructive hover:text-destructive/80" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export function ActionList({ actions, onAddActionItem, onDeleteActionItem, onReorderAction, onUpdateActionItem, variant = 'classic' }: ActionListProps) {
  const [newItem, setNewItem] = useState('');
  const [newHashtags, setNewHashtags] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editText, setEditText] = useState('');
  const [localActions, setLocalActions] = useState<ActionItem[]>([]);

  useEffect(() => {
    setLocalActions(actions);
  }, [actions]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleAddItem = () => {
    if (newItem.trim()) {
      onAddActionItem({
        text: newItem.trim(),
        completed: false,
        hashtags: newHashtags
      });
      setNewItem('');
      setNewHashtags([]);
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    setEditText(localActions[index].text);
  };

  const handleEditSave = (actionItemId: string, tags: string[]) => {
    if (editingIndex !== null && editText.trim()) {
      const itemToUpdate = localActions.find(item => item._id === actionItemId);
      if (itemToUpdate) {
        onUpdateActionItem(actionItemId, {
          text: editText.trim(),
          completed: itemToUpdate.completed,
          order: itemToUpdate.order ?? 0,
          hashtags: tags
        });
        setEditingIndex(null);
        setEditText('');
      }
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localActions.findIndex((item) => item._id === active.id);
    const newIndex = localActions.findIndex((item) => item._id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const updatedActions = [...localActions];
    const [movedItem] = updatedActions.splice(oldIndex, 1);
    updatedActions.splice(newIndex, 0, movedItem);

    const reorderedActions = updatedActions.map((item, index) => ({
      ...item,
      order: index + 1,
    }));

    setLocalActions(reorderedActions);
    onReorderAction(reorderedActions);
  };

  const sortedActions = [...localActions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={() => setEditingIndex(null)} onDragEnd={handleDragEnd}>
        <SortableContext items={sortedActions.map((item) => item._id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-0.5 bg-background/50 rounded-lg p-2">
            {sortedActions.map((item, index) => (
              <SortableActionItem
                key={item._id}
                item={item}
                onDelete={() => onDeleteActionItem(item._id)}
                onEdit={() => startEditing(index)}
                isEditing={editingIndex === index}
                editText={editText}
                onEditTextChange={setEditText}
                onEditSave={(tags) => handleEditSave(item._id, tags)}
                onEditCancel={() => setEditingIndex(null)}
                onUpdateActionItem={onUpdateActionItem}
                variant={variant}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
      <div className="flex gap-2 px-1">
        <div className="flex-1">
          <HashtagInput
            value={newItem}
            onChange={setNewItem}
            onTagsChange={setNewHashtags}
            placeholder="Add action with #tags..."
            onEnterPress={handleAddItem}
            showTags={false}
          />
        </div>
        <Button onClick={handleAddItem}>
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default ActionList;
